// CD-S2: SSS, PhilHealth and Pag-IBIG as people read them — on a payslip, in HR's payroll review and in Finance.
// Each view leads with the decision and the money; how a figure was worked out sits one disclosure away.
// The employee's own deductions are always kept apart from what the employer pays. No government number is shown.
import { useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { DisclosureLink } from "../../../components/Disclosure";
import { focusRing } from "../../../../components/ui/cn";
import { businessApi } from "../../../../services/api";
import {
  AGENCY_NAME, APPARIFY_ROUNDING_NOTE, basisText, contributionState, isDecided, peso,
  reviewFormErrors, shortRuleName, wasRoundedByApparify,
} from "../contributionsView";

// State is carried by a word and its weight. Colour is kept for the two states that need a person.
const STATE_TEXT = {
  ok: "text-secondary",
  reviewed: "text-foreground",
  review: "font-semibold text-warning",
  blocked: "font-semibold text-danger",
};
const muted = "text-secondary";
const strong = "text-foreground";
const quietButton = `inline-flex min-h-10 items-center justify-center rounded-control border border-default bg-surface px-4 text-body-sm font-medium text-foreground hover:border-strong ${focusRing}`;
const primaryButton = `business-primary-action inline-flex min-h-10 items-center justify-center gap-2 rounded-control bg-accent px-4 text-body-sm font-semibold text-on-accent hover:bg-accent-hover disabled:opacity-40 ${focusRing}`;
const day = (value) => (value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-PH", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" })
  : "");

function ReviewForm({ periodId, memberId, line, grossCents, onSaved, onCancel }) {
  // CD-S3: withholding tax is withheld from the employee only; there is no employer amount to enter.
  const employeeOnly = line.agency === "BIR_WHT";
  const id = useId();
  const first = useRef(null);
  const [form, setForm] = useState({
    employee: line.state === "MANUALLY_REVIEWED" ? (line.employeeCents / 100).toFixed(2) : "",
    employer: line.state === "MANUALLY_REVIEWED" ? (line.employerCents / 100).toFixed(2) : "",
    reason: "",
  });
  const [error, setError] = useState(null); // one message at a time: { field, text }
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const set = (key) => (event) => { setForm((current) => ({ ...current, [key]: event.target.value })); if (error?.field === key) setError(null); };
  const agency = AGENCY_NAME[line.agency];
  const hasReference = line.computedEmployeeCents != null;
  const referenceEmployer = Number(line.computedEmployerCents || 0) + Number(line.computedEcCents || 0);

  useEffect(() => { first.current?.focus(); }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const checked = reviewFormErrors({ ...form, grossCents, employeeOnly });
    setFailure("");
    const field = ["employee", "employer", "reason"].find((key) => checked.errors[key]);
    if (field) {
      // Calm validation: name the first thing to fix and put the cursor there.
      setError({ field, text: checked.errors[field] });
      document.getElementById(`${id}-${field}`)?.focus();
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await businessApi.recordPayrollStatutoryReview(periodId, {
        memberId, agency: line.agency, employeeCents: checked.employeeCents, employerCents: checked.employerCents, reason: checked.reason,
      });
      onSaved();
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      setFailure(status === 403
        ? "You cannot enter a reviewed amount. Ask someone who manages payroll."
        : (err?.serverMessage || "The reviewed amount was not saved. Try again."));
    } finally {
      setSaving(false);
    }
  };
  const field = `mt-1 w-full rounded-control border border-default bg-surface px-3 py-2 text-body-sm tabular-nums text-foreground hover:border-strong focus:border-accent focus:outline-none focus:ring-1 focus:ring-focus ${focusRing}`;
  const hint = (key, text) => (
    <p id={`${id}-${key}-help`} role={error?.field === key ? "alert" : undefined}
      className={`mt-1 text-meta ${error?.field === key ? "text-danger" : muted}`}>
      {error?.field === key ? error.text : text}
    </p>
  );
  return (
    <form onSubmit={submit} noValidate aria-label={`Reviewed ${agency} amount`} className="mt-3 space-y-4 border-t border-subtle pt-3 ">
      {hasReference ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className={`text-body-sm ${muted}`}>
            {employeeOnly ? (
              <>The withholding table gives <span className={`tabular-nums ${strong}`}>{peso(line.computedEmployeeCents)}</span> for this payslip.</>
            ) : (
              <>A full month would be <span className={`tabular-nums ${strong}`}>{peso(line.computedEmployeeCents)}</span> for the employee and{" "}
              <span className={`tabular-nums ${strong}`}>{peso(referenceEmployer)}</span> for the employer.</>
            )}
          </p>
          <button type="button" disabled={saving} className={`${quietButton} shrink-0`}
            onClick={() => { setForm((current) => ({ ...current, employee: (line.computedEmployeeCents / 100).toFixed(2), employer: (referenceEmployer / 100).toFixed(2) })); setError(null); }}>
            {employeeOnly ? "Use this amount" : "Use these amounts"}
          </button>
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${id}-employee`} className={`block text-body-sm font-medium ${strong}`}>{employeeOnly ? "Withholding tax (₱)" : "Employee amount (₱)"}</label>
          <input data-ui="input" ref={first} id={`${id}-employee`} type="text" inputMode="decimal" maxLength={12} autoComplete="off" value={form.employee} onChange={set("employee")}
            aria-invalid={error?.field === "employee"} aria-describedby={`${id}-employee-help`} placeholder="0.00" className={field} />
          {hint("employee", employeeOnly ? "Withheld from this payslip. There is no employer amount." : "Deducted from this payslip.")}
        </div>
        {employeeOnly ? null : <div>
          <label htmlFor={`${id}-employer`} className={`block text-body-sm font-medium ${strong}`}>Employer amount (₱)</label>
          <input data-ui="input" id={`${id}-employer`} type="text" inputMode="decimal" maxLength={12} autoComplete="off" value={form.employer} onChange={set("employer")}
            aria-invalid={error?.field === "employer"} aria-describedby={`${id}-employer-help`} placeholder="0.00" className={field} />
          {hint("employer", line.agency === "SSS" ? "Paid by the business, including EC." : "Paid by the business.")}
        </div>}
      </div>
      <div>
        <label htmlFor={`${id}-reason`} className={`block text-body-sm font-medium ${strong}`}>Reason</label>
        <textarea data-ui="input" id={`${id}-reason`} rows={2} maxLength={500} value={form.reason} onChange={set("reason")}
          aria-invalid={error?.field === "reason"} aria-describedby={`${id}-reason-help`} className={field.replace(" tabular-nums", "")} />
        {hint("reason", employeeOnly ? "How you arrived at this amount. Finance sees this." : "How you arrived at these amounts. Finance sees this.")}
      </div>
      {failure ? <p role="alert" className="text-body-sm text-danger">{failure}</p> : null}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button type="submit" disabled={saving} className={primaryButton}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save reviewed amount
        </button>
        <button type="button" disabled={saving} onClick={onCancel} className={quietButton}>Cancel</button>
      </div>
    </form>
  );
}

/** What the collapsed employee card says about contributions: amounts where decided, the state where not. */
export function ContributionGlance({ statutory }) {
  if (!statutory?.closing || !statutory.lines?.length) return null;
  return (
    <span className="mt-0.5 flex flex-wrap gap-x-3 text-meta">
      {statutory.lines.map((line) => {
        const state = contributionState(line);
        return (
          <span key={line.agency} className={isDecided(line) ? muted : STATE_TEXT[state.tone]}>
            {AGENCY_NAME[line.agency]} {isDecided(line) ? peso(line.employeeCents) : state.label.toLowerCase()}
          </span>
        );
      })}
    </span>
  );
}

/** In HR's payroll review: one employee's contributions for the month — the table first, mechanics behind a disclosure. */
export function PayrollContributions({ member, periodId, status, onChanged, focusAgency = "" }) {
  const [editing, setEditing] = useState(focusAgency);
  const anchor = useRef(null);
  useEffect(() => {
    if (!focusAgency) return;
    setEditing(focusAgency);
    anchor.current?.scrollIntoView?.({ block: "center" });
  }, [focusAgency]);
  const statutory = member.statutory;
  if (!statutory || !member.pay) return null;
  const heading = <h4 className={`text-body-sm font-semibold ${strong}`}>Government contributions for {statutory.monthLabel}</h4>;

  if (statutory.alreadyClosedBy) {
    return <div>{heading}<p className={`mt-0.5 text-body-sm ${muted}`}>Already taken on another payslip{statutory.alreadyClosedBy.periodName ? ` (${statutory.alreadyClosedBy.periodName})` : ""}. They are not taken twice.</p></div>;
  }
  if (!statutory.closing) {
    return <div>{heading}<p className={`mt-0.5 text-body-sm ${muted}`}>Not due on this cutoff. They are taken in full on the last cutoff of the month.</p></div>;
  }
  if (!statutory.lines.length) {
    return <div>{heading}<p className={`mt-0.5 text-body-sm ${muted}`}>No pay this month, so no contributions.</p></div>;
  }
  const canReview = status === "computed";
  const others = statutory.sources.filter((source) => !source.own);
  const attention = statutory.lines.filter((line) => !isDecided(line) || line.state === "MANUALLY_REVIEWED");
  const missing = statutory.lines.filter((line) => !line.numberRecorded).map((line) => AGENCY_NAME[line.agency]);
  const cell = "py-2 text-right tabular-nums";
  return (
    <div ref={anchor} className="break-inside-avoid">
      {heading}
      {/* APP-HR-061: no pay means no SSS or Pag-IBIG; PhilHealth rests on the monthly basic salary, so a person decides. */}
      {statutory.noPay ? <p className={`mt-0.5 text-body-sm ${muted}`}>No pay this month, so no SSS or Pag-IBIG.</p> : null}
      <table aria-label={`Government contributions for ${statutory.monthLabel}`} className="figures-table mt-1 w-full border-y border-subtle text-body-sm ">
        <thead>
          <tr className={`text-meta ${muted}`}>
            <th scope="col" className="py-1.5 text-left font-normal">Agency</th>
            <th scope="col" className="py-1.5 text-left font-normal">Status</th>
            <th scope="col" className="py-1.5 text-right font-normal">Employee</th>
            <th scope="col" className="py-1.5 pl-3 text-right font-normal">Employer</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-subtle ">
          {statutory.lines.map((line) => {
            const state = contributionState(line);
            const employerCents = Number(line.employerCents || 0) + Number(line.ecCents || 0);
            return (
              <tr key={line.agency}>
                <th scope="row" className={`py-2 pr-2 text-left font-medium ${strong}`}>{AGENCY_NAME[line.agency]}</th>
                <td className={`py-2 pr-2 ${STATE_TEXT[state.tone]}`}>{state.label}</td>
                <td className={`${cell} ${strong}`}>{isDecided(line) ? peso(line.employeeCents) : <span className={muted}>—</span>}</td>
                <td className={`${cell} pl-3 ${muted}`}>{isDecided(line) ? peso(employerCents) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="border-t border-subtle ">
          <tr>
            <th scope="row" colSpan={2} className={`!whitespace-normal py-2 text-left font-semibold ${strong}`}>Deducted from pay</th>
            <td className={`${cell} font-semibold ${strong}`}>{peso(statutory.employeeTotalCents)}</td>
            <td />
          </tr>
          <tr>
            <th scope="row" colSpan={2} className={`!whitespace-normal pb-2 text-left font-normal ${muted}`}>Paid by the business, not deducted</th>
            <td />
            <td className={`pb-2 text-right tabular-nums ${muted}`}>{peso(statutory.employerTotalCents)}</td>
          </tr>
        </tfoot>
      </table>

      {attention.map((line) => (
        <div key={line.agency} className="mt-3">
          <p className={`text-body-sm font-semibold ${strong}`}>
            {AGENCY_NAME[line.agency]} {line.state === "MANUALLY_REVIEWED" ? "was reviewed by HR" : line.state === "BLOCKED" ? "cannot be worked out" : "needs your review"}
          </p>
          {line.reasonText ? <p className={`text-body-sm ${muted}`}>{line.reasonText}</p> : null}
          {line.reviewSuperseded ? <p className={`text-body-sm ${muted}`}>An earlier reviewed amount no longer applies because the figures changed.</p> : null}
          {line.review ? <p className={`text-body-sm ${muted}`}>Reviewed{line.review.reviewedByName ? ` by ${line.review.reviewedByName}` : ""}{line.review.reviewedAt ? ` on ${day(line.review.reviewedAt)}` : ""}: {line.review.reason}</p> : null}
          {line.state === "BLOCKED" ? null : editing === line.agency ? (
            <ReviewForm periodId={periodId} memberId={member.memberId} line={line} grossCents={member.grossPayCents}
              onCancel={() => setEditing("")} onSaved={() => { setEditing(""); onChanged?.(); }} />
          ) : canReview ? (
            <button type="button" onClick={() => setEditing(line.agency)} className={`mt-2 ${line.state === "MANUALLY_REVIEWED" ? quietButton : primaryButton}`}>
              {line.state === "MANUALLY_REVIEWED" ? `Change the reviewed ${AGENCY_NAME[line.agency]} amount` : `Enter a reviewed ${AGENCY_NAME[line.agency]} amount`}
            </button>
          ) : line.state === "REVIEW_REQUIRED" && status === "collecting_inputs" ? (
            <p className={`mt-1 text-body-sm ${muted}`}>Calculate the payroll first; a reviewed amount can then be entered here.</p>
          ) : null}
        </div>
      ))}

      <DisclosureLink className="mt-2" label="Basis, rules and sources" contentClassName={`space-y-2 pb-1 text-body-sm ${muted}`}>
          <p>
            Worked out from the month's pay of <span className={`tabular-nums ${strong}`}>{peso(statutory.remunerationCents)}</span>
            {others.length ? ` (this cutoff and ${others.map((source) => source.periodName || "an earlier cutoff").join(", ")})` : ""}.
          </p>
          <ul className="space-y-1">
            {statutory.lines.map((line) => (
              <li key={line.agency}>
                <span className={`font-medium ${strong}`}>{AGENCY_NAME[line.agency]}:</span>{" "}
                {[
                  basisText(line),
                  line.agency === "SSS" && isDecided(line) && line.ecCents ? `employer amount includes EC ${peso(line.ecCents)}` : null,
                  line.ruleTitle ? `rule used: ${shortRuleName(line.ruleTitle)}` : null,
                ].filter(Boolean).join("; ")}
                {wasRoundedByApparify(line) ? <span className="block">{APPARIFY_ROUNDING_NOTE}</span> : null}
              </li>
            ))}
          </ul>
          {missing.length ? (
            <p>{missing.join(", ")} {missing.length === 1 ? "number is" : "numbers are"} not recorded for this employee. The amounts are still worked out; add the {missing.length === 1 ? "number" : "numbers"} on the employee's Government &amp; Statutory tab.</p>
          ) : null}
      </DisclosureLink>
    </div>
  );
}


/* ── CD-S3: withholding tax ──────────────────────────────────────────────────────────────────────── */

const WHT_STATE = {
  AUTO_CALCULATED: { label: "Calculated", tone: "ok" },
  MANUALLY_REVIEWED: { label: "Reviewed by HR", tone: "reviewed" },
  REVIEW_REQUIRED: { label: "Needs your review", tone: "review" },
  BLOCKED: { label: "Cannot be worked out", tone: "blocked" },
};

/** Collapsed employee card: the tax withheld, or that it waits for a decision. */
export function WithholdingGlance({ line }) {
  if (!line) return null;
  const decided = line.state === "AUTO_CALCULATED" || line.state === "MANUALLY_REVIEWED";
  return (
    <span className={`mt-0.5 block text-meta ${decided ? muted : STATE_TEXT[WHT_STATE[line.state]?.tone || "review"]}`}>
      {decided ? `Withholding tax ${peso(line.employeeCents)}` : "Withholding tax needs review"}
    </span>
  );
}

/**
 * In HR's payroll review: the tax withheld on this payslip — an employee deduction, never a contribution or an
 * employer cost. The figure and its state first; how it was worked out one disclosure away.
 */
export function PayrollWithholding({ member, periodId, status, onChanged, focus = false }) {
  const line = member.statutory?.withholding;
  const [editing, setEditing] = useState(focus);
  const anchor = useRef(null);
  useEffect(() => {
    if (!focus) return;
    setEditing(true);
    anchor.current?.scrollIntoView?.({ block: "center" });
  }, [focus]);
  if (!line || !member.pay) return null;
  const state = WHT_STATE[line.state] || WHT_STATE.REVIEW_REQUIRED;
  const decided = line.state === "AUTO_CALCULATED" || line.state === "MANUALLY_REVIEWED";
  const canReview = status === "computed";
  const d = line.detail || {};
  const facts = [
    ["Regular pay (base and fixed allowance)", d.regularCents],
    ["Overtime and holiday pay", d.supplementaryCents],
    ["Less your SSS, PhilHealth and Pag-IBIG on this payslip", d.contributionsCents == null ? null : -d.contributionsCents],
    ["Taxable pay", d.taxableCents],
  ].filter(([label, cents]) => cents != null && (cents !== 0 || label === "Taxable pay"));
  return (
    <section ref={anchor} aria-label="Withholding tax" className="break-inside-avoid space-y-2">
      <h4 className={`text-body-sm font-semibold ${strong}`}>Withholding tax</h4>
      {/* The amount sits beside what it is and how it was decided, not at the far edge of the row. */}
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-body-sm">
        {decided ? <span className={`whitespace-nowrap text-money tabular-nums ${strong}`}>{peso(line.employeeCents)}</span> : null}
        <span className={STATE_TEXT[state.tone]}>
          {state.label}
          {line.state === "AUTO_CALCULATED" && d.certainlyZero ? " — taxable pay is within the tax-free bracket." : ""}
          {line.state === "AUTO_CALCULATED" && !d.certainlyZero && d.bracketNo ? ` from the BIR table on taxable pay of ${peso(d.taxableCents)}.` : ""}
        </span>
      </p>
      {line.reasonText && !decided ? <p className={`text-body-sm ${muted}`}>{line.reasonText}</p> : null}
      {line.review ? <p className={`text-body-sm ${muted}`}>Reviewed{line.review.reviewedByName ? ` by ${line.review.reviewedByName}` : ""}: {line.review.reason}</p> : null}
      {line.state === "BLOCKED" ? null : editing && canReview && (line.state === "REVIEW_REQUIRED" || line.state === "MANUALLY_REVIEWED") ? (
        <ReviewForm periodId={periodId} memberId={member.memberId} line={line} grossCents={member.grossPayCents}
          onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged?.(); }} />
      ) : canReview && (line.state === "REVIEW_REQUIRED" || line.state === "MANUALLY_REVIEWED") ? (
        <button type="button" onClick={() => setEditing(true)} className={`mt-1 ${line.state === "MANUALLY_REVIEWED" ? quietButton : primaryButton}`}>
          {line.state === "MANUALLY_REVIEWED" ? "Change the reviewed withholding tax" : "Enter the withholding tax"}
        </button>
      ) : line.state === "REVIEW_REQUIRED" && status === "collecting_inputs" ? (
        <p className={`mt-1 text-body-sm ${muted}`}>Calculate the payroll first; the withholding tax can then be entered here.</p>
      ) : null}
      <DisclosureLink label="How it was worked out" contentClassName={`space-y-2 pb-1 text-body-sm ${muted}`}>
        {facts.length ? (
          <dl className="max-w-md space-y-1">
            {facts.map(([label, cents]) => (
              <div key={label} className="flex justify-between gap-3"><dt>{label}</dt><dd className={`tabular-nums ${strong}`}>{peso(cents)}</dd></div>
            ))}
          </dl>
        ) : null}
        {d.bracketNo ? (
          <p>
            Bracket {d.bracketNo} of the {String(d.frequency || "").replace("_", "-")} table: {peso(d.baseTaxCents)} plus {Number(d.rateBp || 0) / 100}% of the
            taxable pay over {peso(d.bracketLowerCents)}. Only your own contributions lower the taxable pay; what the employer pays never does.
          </p>
        ) : null}
        {d.holidayPolicyKey ? <p>Holiday pay is counted with overtime as supplementary pay (Apparify's reading; the BIR does not name it).</p> : null}
        {line.ruleTitle ? <p>Table used: {line.ruleTitle}.</p> : null}
        <p>Apparify withholds tax per payslip. The year-end tax adjustment is done outside Apparify.</p>
      </DisclosureLink>
    </section>
  );
}
